import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack5";
import useWebotServer from "hooks/useWebotServer";

/**
 * "The realtor just created her own copy of this market ad — record the link."
 *
 * Shared by the listings board's table row and grid card, which render the same
 * "הוסף ל-Webot" action twice. Both need the identical follow-up call, and a
 * copy-pasted second version is how one of them silently stops linking.
 *
 * ## Why the link is a separate call, and not part of the create
 *
 * The server owns `sourceListings` and its flat `sourceListingIds` mirror and
 * writes them TOGETHER — that is the whole invariant behind the mirror. Both
 * fields are in the dashboard's `NotEditableListing`, so the listing form's
 * write path cannot touch either even by accident. The form creates the copy,
 * hands back its id, and this records the relationship.
 *
 * ## Failure is a warning, never an error
 *
 * An unlinked copy is exactly the pre-feature behaviour: both the market ad and
 * her copy show on the board, nothing is lost, and she can attach them later
 * from the listing form's source picker. Failing the creation she just completed
 * would be far worse than losing a link she can re-make.
 */
export function useLinkListingToMarketAd(): (newListingId: string, sourceListingId?: string) => Promise<void> {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { linkListingSource } = useWebotServer();

  return useCallback(
    async (newListingId: string, sourceListingId?: string) => {
      if (!newListingId || !sourceListingId) return;
      try {
        await linkListingSource(newListingId, sourceListingId);
      } catch (error) {
        console.error("Failed to link the new listing to the market ad it was created from:", error);
        enqueueSnackbar(t("listingsBoard.claim.linkFailed"), { variant: "warning" });
      }
    },
    [linkListingSource, enqueueSnackbar, t]
  );
}
